import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {useNavigate} from "@tanstack/react-router";
import {AnankeLozenge} from "@vesta/ananke/components/anankeLozenge";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {
    type CustomerProfileJourneyListItem,
} from "~/components/entityProfile/mockCustomerProfileJourneys";
import type {CustomerProfileTabContext} from "~/components/entityProfile/profileTabRegistry";
import type {ProfileTabRenderProps} from "~/components/entityProfile/types";
import {useWorkspace} from "~/contexts/workspaceContext";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {buildJourneyProfilePath} from "~/utilities/entityProfileNavigation";
import {formatDateTime} from "~/utilities/utilities";

function JourneyListCard({item, onOpen}: {item: CustomerProfileJourneyListItem; onOpen: () => void}) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="grid w-full min-w-0 gap-y-ds-space-100 rounded-[4px] border border-solid border-ds-border bg-ds-background-input p-ds-space-200 text-left"
        >
            <div className="flex flex-wrap items-center justify-between gap-x-ds-space-150 gap-y-ds-space-075">
                <AnankeText weight="bold">{item.title}</AnankeText>
                <AnankeLozenge appearance="default">{item.statusLabel}</AnankeLozenge>
            </div>
            <AnankeText color="color.text.subtlest">Created {item.createdAtLabel}</AnankeText>
            {item.isLinked === true ? (
                <AnankeText color="color.text.subtlest">Linked lifecycle</AnankeText>
            ) : null}
        </button>
    );
}

export function ProfileJourneysTabContent({
    tabRenderProps,
    context,
}: {
    tabRenderProps: ProfileTabRenderProps;
    context: CustomerProfileTabContext;
}) {
    const navigate = useNavigate();
    const {workspace} = useWorkspace();

    if (tabRenderProps.kind !== "customer") {
        return null;
    }

    const linkedJourney =
        context.customer.eventContainer !== null
            ? ({
                  id: context.customer.eventContainer.id,
                  eventContainerTemplateId: context.customer.eventContainer.templateId,
                  title: context.journeyDisplayName,
                  statusLabel: eventContainerStatusToDisplayName(context.customer.eventContainer.status),
                  createdAtLabel: formatDateTime(context.customer.eventContainer.createdAt),
                  isLinked: true,
              } satisfies CustomerProfileJourneyListItem)
            : null;

    const journeyItems =
        linkedJourney !== null
            ? [linkedJourney, ...context.journeys.items.filter((item) => item.id !== linkedJourney.id)]
            : context.journeys.items;

    return (
        <div className="h-full min-h-0 overflow-y-auto overscroll-contain px-ds-space-200 py-ds-space-150">
            {context.journeys.isLoading === true ? (
                <div className="grid gap-y-ds-space-150">
                    <AnankeSkeleton width="100%" height={88} borderRadius="4px" />
                    <AnankeSkeleton width="100%" height={88} borderRadius="4px" />
                </div>
            ) : (
                <div className="grid gap-y-ds-space-150">
                    {journeyItems.map((item) => (
                    <JourneyListCard
                        key={item.id}
                        item={item}
                        onOpen={() => {
                            if (item.isLinked === true && item.eventContainerTemplateId !== null) {
                                void navigate({
                                    to: buildJourneyProfilePath({
                                        workspaceId: workspace.id,
                                        eventContainerId: item.id,
                                        eventContainerTemplateId: item.eventContainerTemplateId,
                                        from: "customers",
                                    }),
                                });
                            }
                        }}
                    />
                    ))}
                </div>
            )}
        </div>
    );
}
